/**
 * `PromotionCommit` — an ephemeral→server sheet promotion, as a **typed value**
 *.
 *
 * A promotion is the most invisible thing the SDK does: the server mints fresh
 * row ids, and `SheetHandle._applyPromotion` re-points the shared
 * `RowLocalId.Registry` in place so every store, subscription and rendered cell
 * keyed by the row OBJECT survives untouched. No tree rebuild, no generation
 * bump, no re-render — the focused editor keeps its caret.
 *
 * The app has to know a promotion happened, because the `SheetListChanged` that
 * rides along would otherwise trigger a whole-sheet rebind and blur that
 * editor. Today it learns by string: the accessor tags its `create` with
 * `SheetsClient.PROMOTE_ACTOR` and the app compares `actor.name ===
 * "sheet-promote"` — a cross-package identity check degraded to a literal,
 * because the accessor is not reachable through a barrel the app imports.
 *
 * This is the typed replacement. It says what the string could only imply: WHICH
 * sheet was promoted, and exactly which wire keys were re-pointed onto which
 * surviving local identities. `from`/`to` are both `RowLocalId` on purpose —
 * after a successful re-key they are the SAME OBJECT (that identity IS the
 * transparency claim, and a test can assert it), and a divergence means the
 * re-key silently missed a row and the server id minted a fresh identity that
 * no store is keyed by.
 *
 * The `PROMOTE_ACTOR` tagging is unchanged and stays authoritative until the
 * app's frame plan moves into `sheets-ui`; this commit is carried alongside
 * it on the seam the app already listens to (`ClientConfig.onSheetPromoted`) so
 * the consumer flip is a separate, revertable step.
 */

import type { RowLocalId } from "@tento-lona/sheets";

import type { SheetId } from "@tento-lona/sheets";

/** One row's re-key: the ephemeral identity, and the identity after promotion. */
export interface RowRemap {
  readonly from: RowLocalId;
  readonly to: RowLocalId;
}

/** Values that go with `RowRemap`: `isTransparent`. */
export namespace RowRemap {
  /**
   * True when the re-key was transparent for this row — the SAME `RowLocalId`
   * object now answers to the server key, so every `Map<RowLocalId, V>` store
   * and every live subscription keyed by it stayed valid.
   */
  export function isTransparent(remap: RowRemap): boolean {
    return remap.from === remap.to;
  }
}

/**
 * What a promotion did, as a value the app can consume instead of an actor
 * name. One `kind` today; the discriminant exists so a future non-transparent
 * promotion (one that genuinely rebuilds) is a new arm rather than a silent
 * change of meaning.
 */
export type PromotionCommit = {
  readonly kind: "transparent-rekey";
  readonly sheetId: SheetId;
  readonly rowRemap: readonly RowRemap[];
};

/** Values that go with `PromotionCommit`: `diverged`. */
export namespace PromotionCommit {
  /** Rows whose identity did NOT survive the re-key — empty on a healthy promote. */
  export function diverged(commit: PromotionCommit): readonly RowRemap[] {
    return commit.rowRemap.filter((remap) => !RowRemap.isTransparent(remap));
  }
}
